// frontend/src/App.tsx
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './index.css'; // Make sure Tailwind's base CSS is imported

// Import components
import Navbar from './components/Navbar';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import HomePage from './pages/HomePage';
import ProfilePage from './pages/ProfilePage';
import MyBetsPage from './pages/MyBetsPage';
import DepositPage from './pages/DepositPage';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import CreateDrawPage from './pages/admin/CreateDrawPage';
import ManageDrawsPage from './pages/admin/ManageDrawsPage';
import ManagePaymentsPage from './pages/admin/ManagePaymentsPage';
import DeclareWinnerPage from './pages/admin/DeclareWinnerPage';
import ManageUsersPage from './pages/admin/ManageUsersPage';

// Import UserInfo type
import { UserInfo } from './types';

// Define the type for the PrivateRoute component's props
interface PrivateRouteProps {
    children: React.ReactNode;
    adminOnly?: boolean; // Optional prop to restrict to admin users
}

// A simple PrivateRoute component for protected routes
const PrivateRoute: React.FC<PrivateRouteProps> = ({ children, adminOnly = false }) => {
    const userInfoString = localStorage.getItem('userInfo') || '{}';
    const user: UserInfo = JSON.parse(userInfoString);

    // If no user or no user ID in userInfo, redirect to login
    if (!user || !user._id) {
        return <Navigate to="/login" />;
    }

    // If route is admin-only and user is not admin, redirect to home
    if (adminOnly && !user.isAdmin) {
        return <Navigate to="/" />;
    }

    return <>{children}</>;
};

function App() {
    return (
        <Router>
            <div className="min-h-screen bg-gray-100">
                <Navbar /> {/* Place Navbar here so it appears on all pages */}
                <Routes>
                    {/* Public Routes */}
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/register" element={<RegisterPage />} />

                    {/* Private User Routes */}
                    <Route
                        path="/"
                        element={
                            <PrivateRoute>
                                <HomePage />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/profile"
                        element={
                            <PrivateRoute>
                                <ProfilePage />
                            </PrivateRoute>
                        }
                    />
                     <Route
                        path="/my-bets" // My Bets Page
                        element={
                            <PrivateRoute>
                                <MyBetsPage />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/deposit" // Deposit Page
                        element={
                            <PrivateRoute>
                                <DepositPage />
                            </PrivateRoute>
                        }
                    />

                    {/* Private Admin Routes */}
                    <Route
                        path="/admin" // Admin Dashboard
                        element={
                            <PrivateRoute adminOnly={true}>
                                <AdminDashboard />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/admin/draws/create" // Admin Create Draw
                        element={
                            <PrivateRoute adminOnly={true}>
                                <CreateDrawPage />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/admin/draws/manage" // Admin Manage Draws
                        element={
                            <PrivateRoute adminOnly={true}>
                                <ManageDrawsPage />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/admin/draws/:id/declare-winner"
                        element={
                            <PrivateRoute adminOnly={true}>
                                <DeclareWinnerPage />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/admin/payments/manage" // Admin Manage Payments
                        element={
                            <PrivateRoute adminOnly={true}>
                                <ManagePaymentsPage />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/admin/users/manage" // NEW: Admin Manage Users
                        element={
                            <PrivateRoute adminOnly={true}>
                                <ManageUsersPage />
                            </PrivateRoute>
                        }
                    />

                    {/* Redirect any unmatched routes to home or login */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </div>
            {/* ToastContainer for displaying notifications */}
            <ToastContainer position="bottom-right" autoClose={5000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
        </Router>
    );
}

export default App;
